import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';

interface SegmentUser {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
}

interface SegmentUsersPage {
  items: SegmentUser[];
  total: number;
  page: number;
  per_page: number;
}

type SegmentKey = 'abandoned-cart' | 'incomplete-kyc' | 'incomplete-wallet';

const SEGMENTS: { key: SegmentKey; label: string; endpoint: string }[] = [
  {
    key: 'abandoned-cart',
    label: 'Abandoned Cart',
    endpoint: '/back-office/segments/abandoned-cart',
  },
  {
    key: 'incomplete-kyc',
    label: 'Incomplete KYC',
    endpoint: '/back-office/segments/incomplete-kyc',
  },
  {
    key: 'incomplete-wallet',
    label: 'Incomplete Wallet Setup',
    endpoint: '/back-office/segments/incomplete-wallet',
  },
];

const PER_PAGE = 20;

export function BroadcastSegmentsPage() {
  const [activeSegment, setActiveSegment] = useState<SegmentKey>(
    SEGMENTS[0].key
  );
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [data, setData] = useState<SegmentUsersPage | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [pushEnabled, setPushEnabled] = useState(true);
  const [inAppEnabled, setInAppEnabled] = useState(true);
  const [sending, setSending] = useState(false);

  const segment = useMemo(
    () => SEGMENTS.find((s) => s.key === activeSegment)!,
    [activeSegment]
  );

  const fetchSegment = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<SegmentUsersPage>(segment.endpoint, {
        params: { page, per_page: PER_PAGE, ...(search && { search }) },
      });
      setData(res.data);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to load segment'));
    } finally {
      setLoading(false);
    }
  }, [segment, page, search]);

  useEffect(() => {
    fetchSegment();
  }, [fetchSegment]);

  // Reset selection state whenever the tab, search, or page changes the
  // underlying result set — stale selections across an unrelated filter
  // would silently broadcast to the wrong people.
  useEffect(() => {
    setSelected(new Set());
    setSelectAllMatching(false);
  }, [activeSegment, search]);

  const switchSegment = (key: SegmentKey) => {
    setActiveSegment(key);
    setPage(1);
    setSearch('');
    setSearchInput('');
  };

  const onSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const toggleUser = (userId: number) => {
    setSelectAllMatching(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const pageFullySelected =
    items.length > 0 && items.every((u) => selected.has(u.user_id));

  const toggleSelectAllOnPage = () => {
    setSelectAllMatching(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (pageFullySelected) {
        items.forEach((u) => next.delete(u.user_id));
      } else {
        items.forEach((u) => next.add(u.user_id));
      }
      return next;
    });
  };

  const selectAllAcrossFilter = async () => {
    setLoading(true);
    try {
      const res = await api.get<number[]>(`${segment.endpoint}/ids`, {
        params: search ? { search } : undefined,
      });
      setSelected(new Set(res.data));
      setSelectAllMatching(true);
    } catch (err: unknown) {
      window.alert(extractErrorMessage(err, 'Failed to select all'));
    } finally {
      setLoading(false);
    }
  };

  const clearSelection = () => {
    setSelected(new Set());
    setSelectAllMatching(false);
  };

  const selectedCount = selectAllMatching ? total : selected.size;

  const sendBroadcast = async () => {
    const channels = [
      ...(pushEnabled ? ['push'] : []),
      ...(inAppEnabled ? ['in_app'] : []),
    ];

    if (
      !title.trim() ||
      !body.trim() ||
      selected.size === 0 ||
      !channels.length
    ) {
      return;
    }

    setSending(true);
    try {
      await api.post('/notification/broadcast', {
        title: title.trim(),
        body: body.trim(),
        channels,
        audience: { user_ids: Array.from(selected) },
      });
      window.alert(`Broadcast sent to ${selectedCount} user(s).`);
      setTitle('');
      setBody('');
      clearSelection();
    } catch (err: unknown) {
      window.alert(extractErrorMessage(err, 'Failed to send broadcast'));
    } finally {
      setSending(false);
    }
  };

  const canSend =
    !sending &&
    selected.size > 0 &&
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    (pushEnabled || inAppEnabled);

  return (
    <div className="px-8 py-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">
          Broadcast Segments
        </h1>
        <p className="text-sm text-slate-500">
          Target users by behavior/status and send them a push and/or in-app
          notification.
        </p>
      </div>

      <div className="mt-6 flex gap-2 border-b border-slate-200">
        {SEGMENTS.map((s) => (
          <button
            key={s.key}
            onClick={() => switchSegment(s.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeSegment === s.key
                ? 'border-brand-500 text-brand-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <form
        onSubmit={onSearchSubmit}
        className="mt-4 flex items-end gap-3 rounded border border-slate-200 bg-white p-4"
      >
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-slate-600">
            Search
          </label>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Name or email"
            className="mt-1 w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <button
          type="submit"
          className="rounded bg-brand-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
        >
          Apply
        </button>
      </form>

      {error && (
        <div className="mt-4 rounded border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between text-sm">
        <div className="text-slate-600">
          {selectedCount > 0
            ? `${selectedCount} user(s) selected${selectAllMatching ? ' (all matching filter)' : ''}`
            : `${total} user(s) match this segment`}
        </div>
        <div className="flex gap-3">
          {!selectAllMatching && total > items.length && (
            <button
              onClick={selectAllAcrossFilter}
              className="text-brand-600 hover:underline"
            >
              Select all {total} matching
            </button>
          )}
          {selectedCount > 0 && (
            <button
              onClick={clearSelection}
              className="text-slate-500 hover:underline"
            >
              Clear selection
            </button>
          )}
        </div>
      </div>

      <div className="mt-2 overflow-hidden rounded border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">
                <input
                  type="checkbox"
                  checked={pageFullySelected}
                  onChange={toggleSelectAllOnPage}
                  disabled={items.length === 0}
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
              </th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td
                  colSpan={3}
                  className="px-4 py-6 text-center text-slate-400"
                >
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan={3}
                  className="px-4 py-6 text-center text-slate-400"
                >
                  No users in this segment.
                </td>
              </tr>
            ) : (
              items.map((u) => (
                <tr key={u.user_id}>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(u.user_id)}
                      onChange={() => toggleUser(u.user_id)}
                      className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    />
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {u.first_name} {u.last_name}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{u.email}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="mt-3 flex items-center justify-between text-sm text-slate-600">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="rounded border border-slate-300 px-3 py-1 disabled:opacity-40"
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="rounded border border-slate-300 px-3 py-1 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

      <div className="mt-6 rounded border border-slate-200 bg-white p-4">
        <div className="text-sm font-medium text-slate-700">
          {selectedCount} user(s) selected
        </div>

        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-slate-600">
              Title
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600">
              Message
            </label>
            <input
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-1.5 text-sm"
            />
          </div>
        </div>

        <div className="mt-3 flex items-center gap-4 text-sm text-slate-700">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={pushEnabled}
              onChange={(e) => setPushEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            Push
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={inAppEnabled}
              onChange={(e) => setInAppEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            In-app
          </label>
        </div>

        <button
          onClick={sendBroadcast}
          disabled={!canSend}
          className="mt-4 rounded bg-brand-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
        >
          {sending ? 'Sending…' : 'Send broadcast'}
        </button>
      </div>
    </div>
  );
}
